import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import ProductCard from "../../components/common/ProductCardComponent/Index";
import DefaultHeader from "../../assests/02 (1).png";
import FilterSidebar from "../../components/FilterSidebar/index";
import ReactPaginate from 'react-paginate';
import { fetchProductsBySubCategory } from '../../Services/CategoryServices';
import Spinner from '../../components/common/loadingSpinnercomponent/Spinner';
import { Image_URL } from "../../config/config"; 
import '../ProductsList/ProductsList.css';

const SubCategoryList = () => {
    const [allProducts, setAllProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [subcategoryName, setSubcategoryName] = useState('');
    const [subcategoryImage, setSubcategoryImage] = useState('');
    const itemsPerPage = 12;
    const { slug } = useParams();

    const [filters, setFilters] = useState({
        category: null,
        size: null,
        availability: null,
        minPrice: null,
        maxPrice: null,
    });

    const [sortOption, setSortOption] = useState('latest');

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const initialFilters = {
            category: urlParams.get('category') || null,
            size: urlParams.get('size') || null,
            availability: urlParams.get('availability') || null,
            minPrice: urlParams.get('min_price') ? parseInt(urlParams.get('min_price'), 10) : null,
            maxPrice: urlParams.get('max_price') ? parseInt(urlParams.get('max_price'), 10) : null,
        };
        const initialSort = urlParams.get('sort') || 'latest';
        setFilters(initialFilters);
        setSortOption(initialSort);
    }, []);

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };

    const buildQueryParams = (filters) => {
        const params = new URLSearchParams();
        params.set('category', filters.category || '');
        params.set('size', filters.size || '');
        params.set('availability', filters.availability || '');
        params.set('min_price', filters.minPrice !== null ? filters.minPrice : '');
        params.set('max_price', filters.maxPrice !== null ? filters.maxPrice : '');
        return params.toString();
    };

    const updateUrlWithQueryString = (queryString, sortOption) => {
        const params = new URLSearchParams(queryString);
        params.set('sort', sortOption);
        const url = `${window.location.pathname}?${params.toString()}`;
        window.history.pushState({ path: url }, '', url);
    };

    const fetchDataWithFilters = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const queryString = buildQueryParams(filters);
            updateUrlWithQueryString(queryString, sortOption);
            const productsData = await fetchProductsBySubCategory(slug, queryString);
            setAllProducts(productsData || []);
            
            if (productsData && productsData.length > 0) {
                setSubcategoryName(productsData[0].SubCategory);
                setSubcategoryImage(`${Image_URL}subcategories/${productsData[0].SubCategoryImage}`);
            }
            
            setCurrentPage(0);
        } catch (error) {
            console.error('Error fetching products:', error);
            setError('Failed to fetch products. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDataWithFilters();
    }, [filters, slug]);

    const getDisplayPrice = (product) => {
        return product.OfferPrice > 0 ? product.OfferPrice : product.Price;
    };

    useEffect(() => {
        if (!isLoading && !error) {
            let sortedProducts = [...allProducts];
            switch (sortOption) {
                case 'price_low':
                    sortedProducts.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b));
                    break;
                case 'price_high':
                    sortedProducts.sort((a, b) => getDisplayPrice(b) - getDisplayPrice(a));
                    break;
                case 'latest':
                default:
                    sortedProducts.sort((a, b) => new Date(b.DateCreated) - new Date(a.DateCreated));
                    break;
            }
            setFilteredProducts(sortedProducts);
            setCurrentPage(0);
        }
    }, [sortOption, allProducts, isLoading, error]);

    useEffect(() => {
        const handlePopState = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const updatedFilters = {
                category: urlParams.get('category') || null,
                size: urlParams.get('size') || null,
                availability: urlParams.get('availability') || null,
                minPrice: urlParams.get('min_price') ? parseInt(urlParams.get('min_price'), 10) : null,
                maxPrice: urlParams.get('max_price') ? parseInt(urlParams.get('max_price'), 10) : null,
            };
            const updatedSort = urlParams.get('sort') || 'latest';
            setFilters(updatedFilters);
            setSortOption(updatedSort);
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const handlePageClick = ({ selected }) => {
        setCurrentPage(selected);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPagedData = () => {
        if (!filteredProducts || filteredProducts.length === 0) {
            return [];
        }
        const offset = currentPage * itemsPerPage;
        return filteredProducts.slice(offset, offset + itemsPerPage);
    };

    const currentItems = getPagedData();
    const endItem = Math.min((currentPage + 1) * itemsPerPage, filteredProducts.length || 0);

    return (
        <div>
            {isLoading && (
                <div className="loading-container">
                    <Spinner />
                </div>
            )}
            {error && (
                <div className="error-container">
                    Error: {error}
                </div>
            )}
            {!isLoading && !error && (
                <>
                    <header className="header-image-container">
                        <img 
                            src={subcategoryImage || DefaultHeader} 
                            alt={subcategoryName || "Header"} 
                            className="img-fluid" 
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = DefaultHeader;
                            }}
                        />
                    </header>
                    <div className="row mt-4 mx-0">
                        <h1 className="h4 heading">
                            {subcategoryName ? `${subcategoryName}` : 'All Products'}
                        </h1>
                        <div className="col text-right">
                            <span className="items-count">
                                Showing {endItem} of {filteredProducts?.length || 0} results
                            </span>
                            <div className="product-dropdown-container">
                                <select
                                    className="custom-dropdown"
                                    value={sortOption}
                                    onChange={(e) => setSortOption(e.target.value)}
                                >
                                    <option value="latest">Sort By Latest</option>
                                    <option value="price_low">Price: Low to High</option>
                                    <option value="price_high">Price: High to Low</option>
                                </select>
                                <i className="fa-solid fa-chevron-down dropdown-icon"></i>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-lg-3">
                                <FilterSidebar 
                                    onFilterChange={handleFilterChange} 
                                    initialFilters={filters} 
                                />
                            </div>
                            <div className="col-lg-9">
                                {currentItems.length === 0 ? (
                                    <div className="no-products">
                                        No products found. Please adjust your filters.
                                    </div>
                                ) : (
                                    <div className="product-list-grid">
                                        {currentItems.map(product => (
                                            <ProductCard key={product.ProductId} product={product} />
                                        ))}
                                    </div>
                                )}
                                {filteredProducts.length > 0 && (
                                    <div className="pagination-container">
                                        <ReactPaginate
                                            previousLabel={<i className="fa-solid fa-chevron-left"></i>}
                                            nextLabel={<i className="fa-solid fa-chevron-right"></i>}
                                            breakLabel="..."
                                            pageCount={Math.ceil(filteredProducts.length / itemsPerPage)}
                                            marginPagesDisplayed={2}
                                            pageRangeDisplayed={5}
                                            onPageChange={handlePageClick}
                                            containerClassName="pagination"
                                            activeClassName="active"
                                            pageClassName="page-item"
                                            pageLinkClassName="page-link"
                                            previousClassName="page-item"
                                            previousLinkClassName="page-link"
                                            nextClassName="page-item"
                                            nextLinkClassName="page-link"
                                            breakClassName="page-item"
                                            breakLinkClassName="page-link"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SubCategoryList;